import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal/Modal";
import ApiManager from "../../ApiManager/ApiManager";
import ViewEntity from "../../components/ViewEntity/ViewEntity";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import FuelDrawerTable from "./FuelDrawerTable";
import ExpenditureDrawerTable from "./ExpenditureDrawerTable";
import PettyCashDrawerTable from "./PettyCashDrawerTable";

const AttendanceDaysDrawer = ({ driverId, from, to, statusType }) => {
  const [dates, setDates] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const { withLoading, isLoading, getMessage } = useComponentLoading();

  React.useEffect(() => {
    if (!loaded) {
      setError(null);
      withLoading('attendance', async () => {
        try {
          const query = new URLSearchParams({
            driver_id: driverId,
            from: from,
            to: to,
          });
          const response = await ApiManager.getAttendanceDriver(query);
          if (!response.ok) throw new Error("Failed to fetch attendance");
          const data = await response.json();
          const filtered = data.filter(item => item.status === statusType);
          setDates(filtered.map(item => item.date));
        } catch (e) {
          setError(e.message);
        }
        setLoaded(true);
      }, 'Loading attendance data...');
    }
  }, [driverId, from, to, statusType, loaded]);

  if (isLoading('attendance')) return <LoadingSpinner message={getMessage('attendance') || 'Loading attendance data...'} />;
  if (error) return <div style={{padding:16, color:'red'}}>Error: {error}</div>;
  if (!dates.length) return <div style={{padding:16}}>No {statusType} days data available.</div>;
  return (
    <div style={{ padding: 16 }}>
      <ul>
        {dates.map((d, i) => <li key={i}>{d}</li>)}
      </ul>
    </div>
  );
};

const Payroll = ({ driverId }) => {
  const [data, setData] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalOpen2, setModalOpen2] = useState(false);
  const [leftover, setLeftover] = useState(0);
  const [extraFields, setExtraFields] = useState([]); // [{label: '', value: ''}]
  const [tillDate, setTillDate] = useState(() => {
    const now = new Date();
    now.setDate(now.getDate()+1);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  });

  // Loading state management
  const {
    loadingStates,
    loadingMessages,
    loadingProgress,
    isLoading,
    withLoading
  } = useComponentLoading({ payroll: false, markPaid: false });

  console.log("leftover: ", leftover);

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };
  const toggleModal2 = () => {
    setModalOpen2(!isModalOpen2);
    if(isModalOpen2){
      fetchData();
    }
  };

  const handleLeftoverChange = (e) => {
    setLeftover(e.target.value);
  };

  const handleDateChange = (e) => {
    const date = new Date(e.target.value);
    date.setDate(date.getDate()+1); // Set to previous day
    date.setHours(0, 0, 0, 0);
    const unixTime = Math.floor(date.getTime() / 1000);
    setTillDate(unixTime);
  };

  // Extra fields handlers
  const handleExtraFieldChange = (idx, key, value) => {
    setExtraFields(fields => fields.map((f, i) => {
      if (i !== idx) return f;
      if (key === 'column_amount') {
        return { ...f, [key]: value === '' ? '' : Number(value) };
      }
      return { ...f, [key]: value };
    }));
  };
  const handleAddExtraField = () => {
    setExtraFields(fields => [...fields, { column_name: '', column_amount: '', column_description: '' }]);
  };
  const handleRemoveExtraField = (idx) => {
    setExtraFields(fields => fields.filter((_, i) => i !== idx));
  };

  const markPayrollPaid = async () => {
    try {
      const payload = extraFields;
      const query = new URLSearchParams({
        driver_id: driverId,
        till: tillDate,
        new_left_over: leftover,
      });
      await withLoading(
        "markPaid",
        async (updateProgress) => {
          updateProgress(20);
          const response = await ApiManager.markPayrollPaid(query, payload);
          updateProgress(80);
          if (!response.ok) throw new Error("Network response was not ok");

          // Get HTML text
          const html = await response.text();
          // Open in new window and trigger print dialog
          const printWindow = window.open("", "_blank");
          printWindow.document.write(html);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();

          updateProgress(100);
        },
        "Marking payroll as paid...",
        10000
      );
    } catch (error) {
      console.log("error", error);
    }
    setModalOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, [driverId]);

  const fetchData = async () => {
    try {
      await withLoading(
        "payroll",
        async (updateProgress) => {
          updateProgress(20);
          const query = new URLSearchParams({
            driver_id: driverId,
            till: tillDate,
          });
          const response = await ApiManager.getPayroll(query);
          updateProgress(60);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(100);
          setData(json);
        },
        "Loading payroll data...",
        10000
      );
    } catch (error) {
      console.log("error", error);
    }
  };

  const formatDate = (dateStr) => {
    // Accepts 'yyyy-mm-dd' or Date object, returns 'dd-mm-yyyy'
    const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getUnix = (val) => {
    if (!val) return undefined;
    if (typeof val === 'number') return val;
    // Handle date in DD-MM-YYYY format
    if (typeof val === 'string') {
      const [day, month, year] = val.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return Math.floor(d.getTime() / 1000);
      }
    }
    // Try regular Date parsing as fallback
    const d = new Date(val);
    if (isNaN(d.getTime())) return undefined;
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };

  const columns = [
    { field: "base_salary", headerName: "Base Salary", type: "number" },
    {
      field: "billing_item_amount",
      headerName: "Billing Item Amount",
      type: "number",
    },
    { field: "allowances", headerName: "Allowances", type: "number" },
    { field: "ot_allowance", headerName: "Overtime Allowance", type: "number" },
    {
      field: "fuel",
      headerName: "Fuel",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => {
        return <FuelDrawerTable 
          row={row} 
          from={getUnix(row.from)} 
          to={getUnix(row.to)} 
          drawerWidth={700} 
        />;
      }
    },
    {
      field: "expenditure",
      headerName: "Expenditure",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => {
        return <ExpenditureDrawerTable 
          row={row} 
          from={getUnix(row.from)} 
          to={getUnix(row.to)} 
          drawerWidth={700} 
        />;
      }
    },
    {
      field: "petty_cash",
      headerName: "Petty Cash",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => {
        return <PettyCashDrawerTable 
          row={row} 
          from={getUnix(row.from)} 
          to={getUnix(row.to)} 
          drawerWidth={700} 
        />;
      }
    },
    { field: "total", headerName: "Total Salary", type: "number" },
    { field: "left_over", headerName: "Left Over", type: "number" },
    {
      field: "present_days",
      headerName: "Present Days",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => (
        <AttendanceDaysDrawer driverId={row.driver_id} from={getUnix(row.from)} to={getUnix(row.to)} statusType="present" />
      )
    },
    {
      field: "absent_days",
      headerName: "Absent Days",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => (
        <AttendanceDaysDrawer driverId={row.driver_id} from={getUnix(row.from)} to={getUnix(row.to)} statusType="absent" />
      )
    },
    {
      field: "unmarked_days",
      headerName: "Unmarked Days",
      type: "number",
      drawer: true,
      drawerContent: (value, row) => (
        <AttendanceDaysDrawer driverId={row.driver_id} from={getUnix(row.from)} to={getUnix(row.to)} statusType="unmarked" />
      )
    },
    {
      field: "from",
      headerName: "From Date",
      info: "Last Paid Day",
      type: "date",
    },
    {
      field: "to",
      headerName: "To Date",
      info: "Salary till this day",
      type: "date",
    },
  ];

  // Helper to convert dd-mm-yyyy to yyyy-mm-dd
  function parseDDMMYYYYtoYYYYMMDD(dateStr) {
    if (!dateStr) return '';
    const [day, month, year] = dateStr.split('-');
    if (!day || !month || !year) return '';
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  if (loadingStates.payroll) {
    return <LoadingSpinner message={loadingMessages.payroll} progress={loadingProgress.payroll} showProgress={true} />;
  }

  return (
    <div className="payroll">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <h2>Driver Payroll</h2>

        <div style={{ display: "flex", gap: "10px" }}>
          <div className="till-date">
            <button className="custom-button" onClick={toggleModal2}>
              Till date
            </button>
            <Modal
              isOpen={isModalOpen2}
              onClose={toggleModal2}
              title="Till Date"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <form>
                  <input
                    type="date"
                    name="tilldate"
                    className="input"
                    min={data && data.from ? parseDDMMYYYYtoYYYYMMDD(data.from) : undefined}
                    onChange={handleDateChange}
                    value={
                      tillDate
                        ? new Date(tillDate * 1000).toISOString().split('T')[0]
                        : ''
                    }
                  />
                </form>
                <button className="custom-button" onClick={toggleModal2}>
                  Done
                </button>
              </div>
            </Modal>
          </div>
          <button className="custom-button" onClick={toggleModal}>
            Generate payroll
          </button>
        </div>
      </div>
      <ViewEntity data={data} columns={columns} />
      {isModalOpen && (
        <Modal isOpen={true} onClose={toggleModal} title="Generate Payroll">
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <form>
              <label htmlFor="leftover">Left over:</label>
              <input
                type="text"
                id="leftover"
                name="leftover"
                onChange={handleLeftoverChange}
                className="input"
              />
              <div style={{ marginTop: 16 }}>
                <label>Additional Rows in Payroll:  </label>
                {extraFields.map((field, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 , marginTop: 8}}>
                    <input
                      type="text"
                      placeholder="Column Name"
                      value={field.column_name}
                      onChange={e => handleExtraFieldChange(idx, 'column_name', e.target.value)}
                      className="input"
                    />
                    <input
                      type="text"
                      placeholder="Column Amount"
                      value={field.column_amount}
                      onChange={e => handleExtraFieldChange(idx, 'column_amount', e.target.value)}
                      className="input"
                    />
                    <input
                      type="text"
                      placeholder="Column Description"
                      value={field.description}
                      onChange={e => handleExtraFieldChange(idx, 'column_description', e.target.value)}
                      className="input"
                    />
                    
                    <button type="button" className="remove-button" onClick={() => handleRemoveExtraField(idx)}>
                      &times;
                    </button>
                  </div>
                ))}
                <button type="button" className="custom-button" onClick={handleAddExtraField} style={{ marginTop: 4 }}>
                  + Add Field
                </button>
              </div>
            </form>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
            >
              <button
                className={`custom-button ${isLoading("markPaid") ? "loading" : ""}`}
                onClick={markPayrollPaid}
                disabled={isLoading("markPaid")}
              >
                {isLoading("markPaid") ? (
                  <LoadingSpinner
                    size="xs"
                    inline={true}
                    message="Marking paid..."
                  />
                ) : (
                  "Mark Paid"
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Payroll;
